import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useTrackDisabledSelectAll } from '@air/analytics';
import { type Board } from '@air/api/types';
import { resetSelectedItemsAction, selectedItemsCountSelector } from '@air/redux-selected-items';

import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import usePrevious from '~/hooks/usePrevious';
import { useAirStore } from '~/utils/ReduxUtils';
import { PRIVATE_BOARD_KEY } from './queries/usePrivateBoard';

export type UseResetViewSelectAllOnBoardIdChangeParams = {
  boardId: string | undefined;
};

export const useResetViewSelectAllOnBoardIdChange = ({ boardId }: UseResetViewSelectAllOnBoardIdChangeParams) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const store = useAirStore();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const previousBoardId = usePrevious(boardId);
  const selectAllRootBoardId = useViewsSelectAllStore((state) => state.selectAllRootBoardId);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);

  const isRootInParentChain = useCallback(
    (startBoardId: string | undefined, rootId: string): boolean => {
      if (!startBoardId) return false;
      if (startBoardId === rootId) return true;

      const cachedQueries = queryClient.getQueriesData<Board>({
        queryKey: [PRIVATE_BOARD_KEY],
      });

      const parentMap = new Map<string, string | null>();
      for (const [, data] of cachedQueries) {
        if (data?.id && data.parentId !== undefined) {
          parentMap.set(data.id, data.parentId);
        }
      }

      let currentId: string | null = startBoardId;
      const visited = new Set<string>();

      while (currentId && !visited.has(currentId)) {
        if (currentId === rootId) return true;
        visited.add(currentId);
        currentId = parentMap.get(currentId) ?? null;
      }

      return false;
    },
    [queryClient],
  );

  // We don't use Redux ancestors because they can be stale after board moves.
  // parentId traversal is reliable since useMoveBoards updates the cache.
  const isInSelectAllSubtree = useMemo(
    () =>
      !selectAllRootBoardId ||
      boardId === selectAllRootBoardId ||
      (!!boardId && isRootInParentChain(boardId, selectAllRootBoardId)),
    [boardId, selectAllRootBoardId, isRootInParentChain],
  );

  const shouldPreserveSelection = includeNestedItems && isInSelectAllSubtree;

  useEffect(() => {
    if (previousBoardId !== boardId) {
      const { selectAll, resetSelectAll } = useViewsSelectAllStore.getState();

      if (selectAll && !shouldPreserveSelection) {
        trackDisabledSelectAll({
          trigger: 'navigation',
          totalSelectionCount: selectedItemsCountSelector(store.getState()),
        });
        resetSelectAll();
        dispatch(resetSelectedItemsAction());
        return;
      }

      if (selectAll) {
        return;
      }

      resetSelectAll();
    }
  }, [boardId, previousBoardId, shouldPreserveSelection, dispatch, store, trackDisabledSelectAll]);
};
